import throttle from 'lodash.throttle';
import type { MachineConfig } from 'xstate';
import { assign, createMachine as machineGenerator } from 'xstate';

import { Menu } from 'src/modules/designStudio/constants/menu';
import type { DesignBuilding } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { macheteBuildingRef } from 'src/modules/designStudio/machines/building/ref';
import { macheteViewerRef } from 'src/modules/designStudio/machines/viewer/ref';
import { Mode } from 'src/modules/designStudio/providers';
import {
  EventType,
  Page,
  emitPageAction,
  modelInteracted,
  moduleViewed,
} from 'src/shared/telemetry';

import {
  changeCameraFocus,
  getFocusOptionsForMenu,
  listenForEventFactory,
  renderMachete,
} from '../../helpers/machete';
import { sharedOptions } from '../sharedOptions';

import {
  getRequiredStyleUpdatesFromHistoryEvent,
  sendBuildingLoadTrackingEvents,
} from './helpers';
import type {
  BuildingInteraction,
  ChangeViewEvent,
  Context,
  Event,
  Schema,
  SetBuildingAndViewerEvent,
} from './types';

const THROTTLE_TIME = 3000;
const OPEN_MENU_TRANSITION_TIME = 300;
const OPEN_MENU_DELAY = 350;

export const initialContext = {
  building: null,
  canvasRef: null,
  originalFeaturesStyles: {},
  viewer: null,
  geometry: null,
  metadata: null,
  textures: null,
  splitClient: null,
  mode: Mode.DEFAULT,
  loadingStyleUpdates: 0,
  savedStyleUpdates: 0,
  sentHistoryLoadedEvent: false,
  sentSavedStylesAppliedEvent: false,
  inspireMeImageURL: '',
  inspireMeDesignNameToRestore: '',
  inspireMeFrozenRenderURL: '',
  inspireMePastImageURLs: [],
};

const buildInteractionListener =
  (interactionEvent: BuildingInteraction) => (ctx: Context) => {
    const trackModelInteracted = throttle(
      () =>
        modelInteracted({
          interaction_type: interactionEvent,
          job_id: ctx.jobId,
          design_studio_mode: ctx.mode,
          ...ctx.trackingMetadata,
        }),
      THROTTLE_TIME,
    );

    const removeListener = listenForEventFactory(ctx.viewer)(
      interactionEvent,
      trackModelInteracted,
    );

    return removeListener;
  };

export const options = {
  services: {
    renderModel: async (context: Context) => {
      const { canvasRef, textures, geometry, metadata, splitClient, mode } =
        context;

      const { building, viewer, originalFeaturesStyles } = await renderMachete({
        canvasRef,
        geometry,
        metadata,
        textures,
        splitClient,
        mode,
      });

      return { building, viewer, originalFeaturesStyles };
    },
    trackModelInteractions: (context: Context) => () => {
      const cleanupTrackZoom = buildInteractionListener('zoom')(context);
      const cleanupTrackRotate = buildInteractionListener('rotate')(context);
      const cleanupTrackPan = buildInteractionListener('pan')(context);

      return () => {
        cleanupTrackZoom();
        cleanupTrackRotate();
        cleanupTrackPan();
      };
    },
  },
  actions: {
    // log error/exception to Sentry
    logError: sharedOptions.logError,
    setBuildingAndViewer: assign((_: Context, event: Event) => {
      macheteBuildingRef.current = (event as SetBuildingAndViewerEvent).data
        .building as DesignBuilding;
      macheteViewerRef.current = (
        event as SetBuildingAndViewerEvent
      ).data.viewer;
      return (event as SetBuildingAndViewerEvent).data;
    }),
    sendBuildingLoadedEvent: (ctx: Context) => {
      const attrs = {
        designStudioMode: ctx.mode,
      };

      moduleViewed({
        page_or_screen_name: Page.DESIGN_STUDIO,
        job_id: ctx.jobId,
        feature: 'design studio building',
      });
      emitPageAction(EventType.BUILDING_FULLY_LOADED, attrs);
    },
    sendLoadBuildingStartedEvent: (ctx: Context) => {
      const attrs = {
        designStudioMode: ctx.mode,
      };

      emitPageAction(EventType.LOAD_BUILDING_STARTED, attrs);
    },
    sendHistoryLoadedEvent: assign((_ctx: Context, event: Event) => ({
      sentHistoryLoadedEvent: true,
      savedStyleUpdates: getRequiredStyleUpdatesFromHistoryEvent(event),
    })),
    resizeView: async ({ viewer }: Context, event: Event) => {
      const { menu } = event as ChangeViewEvent;

      if (!viewer) return;
      if (menu !== null) {
        await viewer.resize(OPEN_MENU_TRANSITION_TIME, OPEN_MENU_DELAY);
      } else {
        await viewer.resize();
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateStyles: assign((ctx: Context, _: Event) =>
      sendBuildingLoadTrackingEvents(ctx),
    ),
    focusActiveMenu: async (ctx: Context, event: Event) => {
      const { building, viewer } = ctx;
      const { menu } = event as ChangeViewEvent;

      const ActiveFeatures = new Set([
        Menu.ROOF,
        Menu.TRIM,
        Menu.WALL,
        Menu.WINDOW,
      ]);

      if (menu !== null && !ActiveFeatures.has(menu)) {
        return;
      }

      const { focusElement, focusOptions } = await getFocusOptionsForMenu(
        building,
        viewer,
        menu,
      );
      const delay = 0;
      void changeCameraFocus(viewer, focusElement, focusOptions, delay);
    },
  },
  guards: {
    hasTextures: (ctx: Context) => ctx.textures !== null,
    hasNotSentHistoryLoadedEvent: (ctx: Context) => !ctx.sentHistoryLoadedEvent,
  },
};

export const config: MachineConfig<Context, Schema, Event> = {
  id: 'building',
  initial: 'renderingModel',
  context: {
    ...initialContext,
  },
  states: {
    renderingModel: {
      entry: 'sendLoadBuildingStartedEvent',
      invoke: {
        src: 'renderModel',
        onDone: {
          target: 'idle',
          actions: ['setBuildingAndViewer', 'sendBuildingLoadedEvent'],
        },
        onError: {
          actions: 'logError',
        },
      },
    },
    idle: {
      invoke: {
        src: 'trackModelInteractions',
      },
      on: {
        UPDATE_STYLES: {
          actions: ['updateStyles'],
        },
        CHANGE_VIEW: {
          actions: ['resizeView', 'focusActiveMenu'],
        },
        HISTORY_LOADED: {
          actions: ['sendHistoryLoadedEvent'],
          cond: 'hasNotSentHistoryLoadedEvent',
        },
      },
    },
  },
};

const createMachine = () => machineGenerator(config, options);

export { createMachine };

export * from './types';
